import { GAMMA_LONG_CHAIN } from "./gamma";
import { summariseDailyQuality } from "./hourly-windows";
// PRONE FOIL scoring (formerly the generic "foil" model).
//
// A prior audit found this model is a well-calibrated prone-foil scorer that
// was wearing a "surf foil / downwind / wing" label it did not earn: the
// energy curve peaking at 0.8-1.2m and the wind-is-bad orientation are right
// for prone foiling and wrong for downwind (which wants 1.5-3m open-ocean
// bumps and moderate wind) and winging (which wants wind, full stop). Those
// disciplines now have their own models in downwind-foil-scoring.ts and
// wing-foil-scoring.ts. This file keeps the v5 export names because "foil"
// remains the internal sport key for the prone discipline.
//
// The 11-factor multiplicative chain is compressed with adaptive gamma
// (LONG_CHAIN parameters) so a "bit of everything" day reads ~6.5 rather
// than 4.8. See lib/conditions/gamma.ts for the mechanism and calibration.

import type { SunTimes } from "./daylight";
import { isDark } from "./daylight";
import { composeScore, defineChain } from "./compose-score";
import { classifySuppliedCurrent } from "./seaward-current";
import { classifyModelFog } from "./model-fog";

// Prone foil's chain, declared as data (tracker P3-19); classes declared, not
// enforced (Phase 7). GROUPING: the original assembly accumulated the 11
// factor terms left to right and only then multiplied that product as the
// RIGHT operand of (base / 100) * factorProduct. Declaring base LAST keeps
// this bit-exact: IEEE 754 two-operand multiplication is commutative, so
// factorProduct * (base / 100) === (base / 100) * factorProduct exactly.
export const PRONE_FOIL_CHAIN = defineChain({
  sport: "prone-foil",
  gamma: GAMMA_LONG_CHAIN, // Pinned calibration; factor-list refactors must not change the scale.
  factors: [
    { key: "period", class: "driver" },
    { key: "direction", class: "driver" },
    { key: "windSpeed", class: "driver" },
    { key: "gust", class: "driver" },
    { key: "windDirection", class: "modifier" },
    { key: "chop", class: "driver" },
    { key: "tide", class: "driver" },
    { key: "current", class: "driver" },
    { key: "rain", class: "modifier", note: "floor 0.65 breaches 0.85; P7-3" },
    { key: "visibility", class: "driver", note: "sub-500m tier migration upheld (04 B-vis): foiling blind near rocks and other users degrades the medium, so it is kept deep, not a floored enjoyment modifier (P7-1). P7-4 recalibrates the 800-2000m coastal-mist zone to a bounded ~0.85 dent (charging starts ~2000m); see foilVisibilityFactor" },
    { key: "goldenHour", class: "structural", note: "bounded bonus (04 B-golden)" },
    { key: "base", class: "driver", note: "swell energy base, value base/100; multiplied last: commutative-exact to the original (base/100) * factorProduct grouping" },
  ],
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * Math.max(0, Math.min(1, t));
}

// ---------------------------------------------------------------------------
// Named thresholds & constants
// ---------------------------------------------------------------------------

const SWELL_DEAD_FLAT_M = 0.1;
const SWELL_MARGINAL_M = 0.3;
const SWELL_RIDEABLE_M = 0.5;
const SWELL_GOOD_M = 0.7;
const SWELL_PEAK_M = 1.0;
const SWELL_PEAK_END_M = 1.3;
const SWELL_SWEET_SPOT_END_M = 1.5;
const SWELL_DECLINING_M = 2.0;
const SWELL_CHALLENGING_M = 2.5;
const SWELL_DANGEROUS_M = 3.5;
const SWELL_FLOOR = 3;

const PERIOD_SIGMOID_CENTER_S = 7.0;
const PERIOD_SIGMOID_STEEPNESS = 0.7;
const PERIOD_FLOOR = 0.60;
const PERIOD_CEILING = 1.05;

const DIRECTION_SIGMA_DEG = 75;
const DIRECTION_FLOOR = 0.50;
// Unknown swell direction or unconfigured optimal direction: neutral, never a
// penalty. The previous 0.85 capped unconfigured spots at 8.5 permanently.
const DIRECTION_UNKNOWN_DEFAULT = 1.0;

const WIND_IDEAL_MAX_KMH = 15;
const WIND_MODERATE_KMH = 25;
const WIND_STRONG_KMH = 35;
const WIND_EXTREME_KMH = 45;
const WIND_STORM_KMH = 55;
const WIND_FLOOR = 0.10;

const GUST_GATE_KMH = 10;
const GUST_RATIO_OK = 1.3;
const GUST_RATIO_HIGH = 1.6;
const GUST_PENALTY_MODERATE = 0.85;
const GUST_PENALTY_HEAVY = 0.80;
const GUST_ABSOLUTE_CAP_KMH = 50;
const GUST_ABSOLUTE_PENALTY = 0.65;

const WIND_DIR_MAX_ONSHORE_PENALTY = 0.03;
const WIND_DIR_UNKNOWN_DEFAULT = 1.0;

const CHOP_NEGLIGIBLE_M = 0.15;
const CHOP_LIGHT_M = 0.4;
const CHOP_MODERATE_M = 0.7;
const CHOP_HEAVY_M = 1.0;
const CHOP_EXTREME_M = 1.5;
const CHOP_FLOOR = 0.08;

const CURRENT_SAFE_MS = 0.3;
const CURRENT_NOTICEABLE_MS = 0.5;
const CURRENT_STRONG_MS = 0.8;
const CURRENT_DANGEROUS_MS = 1.3;
const CURRENT_FLOOR = 0.30;

const RAIN_LIGHT_MM = 2;
const RAIN_MODERATE_MM = 5;
const RAIN_HEAVY_MM = 10;
const RAIN_FLOOR = 0.65;

// P7-4 (audit 04 V3): visibility STAYS a quality driver (sub-500m fog genuinely
// degrades prone foiling - blind set-lining/bump-reading near rocks and users -
// the tier migration upheld at 04 B-vis). Only the mid-zone is recalibrated:
// coastal mist at 800-2000m does not degrade the medium for an expert (bumps are
// read at 50-200m, not 2km), so the dent there is bounded near the 0.85 enjoyment
// floor and charging starts only below ~2000m; the genuine-fog migration to the
// 0.40 floor resumes below ~800m. Was: 0.88 at 2000m, 0.70 at 1000m (a silent
// ~21-point dock on a perfect mist day).
const VIS_CLEAR_M = 2000; // at/above: no charge (coastal mist, set-spotting unimpeded)
const VIS_MIST_M = 800; // 800-2000m mist: bounded modifier-style dent >= VIS_MIST_FLOOR
const VIS_DANGEROUS_M = 500; // below 800m genuine fog migrates toward the floor
const VIS_MIST_FLOOR = 0.85; // the ~0.85 enjoyment floor for the mist band
const VIS_FLOOR = 0.40; // sub-500m dense fog (tier migration upheld, 04 B-vis)

const GOLDEN_HOUR_WIND_GATE_KMH = 10;
const GOLDEN_HOUR_WINDOW_H = 1.5;
const GOLDEN_HOUR_MAX_BONUS = 0.05;

const DAILY_BEST_WINDOW_WEIGHT = 0.65;
const DAILY_BEST_WINDOW_HOURS = 3;

// ---------------------------------------------------------------------------
// Factor 1: Energy base (0–100)
//
// Swell height determines the base foiling energy. Sweet spot 0.5–1.5m,
// peak at 0.8–1.2m. Below 0.3m is marginal, above 2.5m is challenging.
// Piecewise linear with smooth transitions matching the user's guidance
// that foils need LESS swell than surfing.
// ---------------------------------------------------------------------------

export function foilEnergyBase(h: number): number {
  if (h <= SWELL_DEAD_FLAT_M) return 0;
  if (h <= SWELL_MARGINAL_M) return lerp(0, 18, (h - SWELL_DEAD_FLAT_M) / (SWELL_MARGINAL_M - SWELL_DEAD_FLAT_M));
  if (h <= SWELL_RIDEABLE_M) return lerp(18, 65, (h - SWELL_MARGINAL_M) / (SWELL_RIDEABLE_M - SWELL_MARGINAL_M));
  if (h <= SWELL_GOOD_M) return lerp(65, 92, (h - SWELL_RIDEABLE_M) / (SWELL_GOOD_M - SWELL_RIDEABLE_M));
  if (h <= SWELL_PEAK_M) return lerp(92, 100, (h - SWELL_GOOD_M) / (SWELL_PEAK_M - SWELL_GOOD_M));
  if (h <= SWELL_PEAK_END_M) return lerp(100, 92, (h - SWELL_PEAK_M) / (SWELL_PEAK_END_M - SWELL_PEAK_M));
  if (h <= SWELL_SWEET_SPOT_END_M) return lerp(92, 78, (h - SWELL_PEAK_END_M) / (SWELL_SWEET_SPOT_END_M - SWELL_PEAK_END_M));
  if (h <= SWELL_DECLINING_M) return lerp(78, 35, (h - SWELL_SWEET_SPOT_END_M) / (SWELL_DECLINING_M - SWELL_SWEET_SPOT_END_M));
  if (h <= SWELL_CHALLENGING_M) return lerp(35, 12, (h - SWELL_DECLINING_M) / (SWELL_CHALLENGING_M - SWELL_DECLINING_M));
  if (h <= SWELL_DANGEROUS_M) return lerp(12, SWELL_FLOOR, (h - SWELL_CHALLENGING_M) / (SWELL_DANGEROUS_M - SWELL_CHALLENGING_M));
  return SWELL_FLOOR;
}

// ---------------------------------------------------------------------------
// Factor 2: Period (0.60–1.05)
//
// Longer period swell is smoother and more predictable. Sigmoid curve
// centered at 7s. Foiling is much more forgiving of short period than
// surfing - a 6s wind swell loses ~25% vs ~57% for surf.
// ---------------------------------------------------------------------------

export function foilPeriodFactor(period: number): number {
  const range = PERIOD_CEILING - PERIOD_FLOOR;
  return PERIOD_FLOOR + range / (1 + Math.exp(-PERIOD_SIGMOID_STEEPNESS * (period - PERIOD_SIGMOID_CENTER_S)));
}

// ---------------------------------------------------------------------------
// Factor 3: Swell direction (0.50–1.0)
//
// Gaussian falloff with σ=75°. Foils ride energy from any direction -
// they don't need organised breaking waves. Much wider than surf (σ=35°).
// ---------------------------------------------------------------------------

export function foilDirectionFactor(
  swellDirection: number,
  spotOptimalDirection: number | null,
): number {
  if (spotOptimalDirection === null) return DIRECTION_UNKNOWN_DEFAULT;
  let diff = Math.abs(swellDirection - spotOptimalDirection);
  if (diff > 180) diff = 360 - diff;
  return Math.max(DIRECTION_FLOOR, Math.exp(-(diff * diff) / (2 * DIRECTION_SIGMA_DEG * DIRECTION_SIGMA_DEG)));
}

// ---------------------------------------------------------------------------
// Factor 4: Wind speed (0.10–1.0)
//
// Combined foiling sweet spot is calm-to-moderate. Surf foiling prefers
// 0–15 km/h, wing foiling 12–25 km/h. Above 35 km/h all subdisciplines
// struggle. Piecewise linear for clarity.
// ---------------------------------------------------------------------------

export function foilWindSpeedFactor(speed: number): number {
  if (speed <= WIND_IDEAL_MAX_KMH) return 1.0;
  if (speed <= WIND_MODERATE_KMH) return lerp(1.0, 0.90, (speed - WIND_IDEAL_MAX_KMH) / (WIND_MODERATE_KMH - WIND_IDEAL_MAX_KMH));
  if (speed <= WIND_STRONG_KMH) return lerp(0.90, 0.60, (speed - WIND_MODERATE_KMH) / (WIND_STRONG_KMH - WIND_MODERATE_KMH));
  if (speed <= WIND_EXTREME_KMH) return lerp(0.60, 0.25, (speed - WIND_STRONG_KMH) / (WIND_EXTREME_KMH - WIND_STRONG_KMH));
  if (speed <= WIND_STORM_KMH) return lerp(0.25, WIND_FLOOR, (speed - WIND_EXTREME_KMH) / (WIND_STORM_KMH - WIND_EXTREME_KMH));
  return WIND_FLOOR;
}

// ---------------------------------------------------------------------------
// Factor 5: Gust penalty (0.65–1.0)
//
// Sudden lift changes on a hydrofoil at speed are dangerous. Ratio-based:
// 10→25 km/h is harmless but 30→45 km/h is dangerous despite the same
// absolute gap. Gated on sustained > 10 km/h.
// ---------------------------------------------------------------------------

export function foilGustPenalty(sustained: number, gust: number): number {
  if (sustained <= GUST_GATE_KMH) return 1.0;
  const ratio = gust / sustained;
  let factor: number;
  if (ratio <= GUST_RATIO_OK) {
    factor = 1.0;
  } else if (ratio <= GUST_RATIO_HIGH) {
    factor = lerp(1.0, GUST_PENALTY_MODERATE, (ratio - GUST_RATIO_OK) / (GUST_RATIO_HIGH - GUST_RATIO_OK));
  } else {
    factor = GUST_PENALTY_HEAVY;
  }
  if (gust >= GUST_ABSOLUTE_CAP_KMH) factor = Math.min(factor, GUST_ABSOLUTE_PENALTY);
  return factor;
}

// ---------------------------------------------------------------------------
// Factor 6: Wind direction (0.97–1.0)
//
// Near-irrelevant for foiling. Foils ride above the surface; onshore wind
// barely affects them (and wing foilers need it). Pure onshore loses 3%.
// ---------------------------------------------------------------------------

export function foilWindDirectionFactor(
  windDirection: number,
  shoreNormal: number | null,
): number {
  if (shoreNormal === null) return WIND_DIR_UNKNOWN_DEFAULT;
  const diff = ((windDirection - shoreNormal) * Math.PI) / 180;
  const offshoreComponent = Math.cos(diff);
  if (offshoreComponent >= 0) return 1.0;
  return 1.0 - WIND_DIR_MAX_ONSHORE_PENALTY * Math.abs(offshoreComponent);
}

// ---------------------------------------------------------------------------
// Factor 7: Wind chop (0.08 to 1.0)
//
// An airborne rider can pass over small chop, but a prone session still needs
// paddling, take-offs and repeated touchdowns. Large short waves disrupt those
// phases, even when the underlying swell remains usable.
// ---------------------------------------------------------------------------

export function foilChopFactor(windWaveHeight: number): number {
  if (windWaveHeight <= CHOP_NEGLIGIBLE_M) return 1.0;
  if (windWaveHeight <= CHOP_LIGHT_M) return lerp(1.0, 0.90, (windWaveHeight - CHOP_NEGLIGIBLE_M) / (CHOP_LIGHT_M - CHOP_NEGLIGIBLE_M));
  if (windWaveHeight <= CHOP_MODERATE_M) return lerp(0.90, 0.45, (windWaveHeight - CHOP_LIGHT_M) / (CHOP_MODERATE_M - CHOP_LIGHT_M));
  if (windWaveHeight <= CHOP_HEAVY_M) return lerp(0.45, 0.20, (windWaveHeight - CHOP_MODERATE_M) / (CHOP_HEAVY_M - CHOP_MODERATE_M));
  if (windWaveHeight <= CHOP_EXTREME_M) return lerp(0.20, CHOP_FLOOR, (windWaveHeight - CHOP_HEAVY_M) / (CHOP_EXTREME_M - CHOP_HEAVY_M));
  return CHOP_FLOOR;
}

// ---------------------------------------------------------------------------
// Factor 8: Tide (0.45–1.0)
//
// Mast clearance at low tide. Foil masts are 60–90cm, so shallow reef
// at low tide is a hard functional limit (can't ride, not just dangerous).
// Three break-type tiers.
// ---------------------------------------------------------------------------

export function foilTideFactor(
  tidePosition: number,
  breakType: string,
): number {
  switch (breakType) {
    case "reef":
    case "reef break":
      if (tidePosition < 0.15) return 0.45;
      if (tidePosition < 0.30) return lerp(0.45, 0.80, (tidePosition - 0.15) / 0.15);
      if (tidePosition < 0.50) return lerp(0.80, 1.0, (tidePosition - 0.30) / 0.20);
      return 1.0;
    case "point":
    case "point break":
      if (tidePosition < 0.15) return 0.55;
      if (tidePosition < 0.35) return lerp(0.55, 1.0, (tidePosition - 0.15) / 0.20);
      return 1.0;
    default:
      if (tidePosition < 0.10) return 0.65;
      if (tidePosition < 0.25) return lerp(0.65, 1.0, (tidePosition - 0.10) / 0.15);
      return 1.0;
  }
}

// ---------------------------------------------------------------------------
// Factor 9: Current (0.30–1.0)
//
// Strong currents make foiling harder - fighting drift, unpredictable foil
// behaviour, getting swept. Moderate current (0.1–0.3 m/s) is neutral.
// ---------------------------------------------------------------------------

export function foilCurrentFactor(velocity: number): number {
  if (velocity <= CURRENT_SAFE_MS) return 1.0;
  if (velocity <= CURRENT_NOTICEABLE_MS) return lerp(1.0, 0.88, (velocity - CURRENT_SAFE_MS) / (CURRENT_NOTICEABLE_MS - CURRENT_SAFE_MS));
  if (velocity <= CURRENT_STRONG_MS) return lerp(0.88, 0.65, (velocity - CURRENT_NOTICEABLE_MS) / (CURRENT_STRONG_MS - CURRENT_NOTICEABLE_MS));
  if (velocity <= CURRENT_DANGEROUS_MS) return lerp(0.65, 0.35, (velocity - CURRENT_STRONG_MS) / (CURRENT_DANGEROUS_MS - CURRENT_STRONG_MS));
  return CURRENT_FLOOR;
}

// ---------------------------------------------------------------------------
// Factor 10: Rain (0.65–1.0)
//
// Reduces visibility and comfort. Minor impact on foiling quality itself.
// ---------------------------------------------------------------------------

export function foilRainFactor(precip: number): number {
  if (precip <= 0) return 1.0;
  if (precip <= RAIN_LIGHT_MM) return lerp(1.0, 0.93, precip / RAIN_LIGHT_MM);
  if (precip <= RAIN_MODERATE_MM) return lerp(0.93, 0.80, (precip - RAIN_LIGHT_MM) / (RAIN_MODERATE_MM - RAIN_LIGHT_MM));
  if (precip <= RAIN_HEAVY_MM) return lerp(0.80, RAIN_FLOOR, (precip - RAIN_MODERATE_MM) / (RAIN_HEAVY_MM - RAIN_MODERATE_MM));
  return RAIN_FLOOR;
}

// ---------------------------------------------------------------------------
// Factor 11: Visibility (0.40-1.0) - a quality driver (P7-4, audit 04 V3)
//
// Visibility is scored as quality, not safety: below ~800m genuine fog degrades
// the foiling itself for a competent rider (you read approaching lines and bump
// trains tens of metres out and navigate back to the peak by land sights, both
// of which sub-500m fog kills), so the curve holds the 0.40 floor there (the
// tier migration upheld at 04 B-vis). At 800-2000m coastal mist set-spotting is
// unimpeded, so the dent is bounded near the 0.85 enjoyment floor; >= 2000m no
// charge. The separate collision/navigation HAZARD is carried by the red < 500m
// / amber < 1000m flag in foilSafetyIndicators, never by this factor.
// ---------------------------------------------------------------------------

export function foilVisibilityFactor(vis: number): number {
  if (vis >= VIS_CLEAR_M) return 1.0;
  // Coastal mist (800-2000m): gentle dent, bounded at the 0.85 enjoyment floor.
  if (vis >= VIS_MIST_M) return lerp(VIS_MIST_FLOOR, 1.0, (vis - VIS_MIST_M) / (VIS_CLEAR_M - VIS_MIST_M));
  // Genuine fog (500-800m): the medium starts to degrade; migrate to the floor.
  if (vis >= VIS_DANGEROUS_M) return lerp(VIS_FLOOR, VIS_MIST_FLOOR, (vis - VIS_DANGEROUS_M) / (VIS_MIST_M - VIS_DANGEROUS_M));
  return VIS_FLOOR; // sub-500m dense fog
}

// ---------------------------------------------------------------------------
// Factor 12: Golden hour bonus (1.0–1.05)
//
// Dawn and dusk glass-off sessions are magic for foiling. Multiplicative
// bonus, wind-gated (only applies when wind ≤ 10 km/h).
// ---------------------------------------------------------------------------

export function foilGoldenHourFactor(
  hour: number,
  sunrise: number,
  sunset: number,
  windSpeed: number,
): number {
  if (windSpeed > GOLDEN_HOUR_WIND_GATE_KMH) return 1.0;
  const nearest = Math.min(
    Math.abs(hour - sunrise),
    Math.abs(hour - sunset),
  );
  if (nearest > GOLDEN_HOUR_WINDOW_H) return 1.0;
  return 1.0 + GOLDEN_HOUR_MAX_BONUS * (1.0 - nearest / GOLDEN_HOUR_WINDOW_H);
}

// ---------------------------------------------------------------------------
// Labels (applied to 0–10 daily score)
// ---------------------------------------------------------------------------

export function foilLabelV5(score: number): string {
  if (score >= 9.0) return "Hero";
  if (score >= 7.5) return "Firing";
  if (score >= 6.5) return "Great";
  if (score >= 5.0) return "Good";
  if (score >= 3.5) return "Fair";
  if (score >= 2.0) return "Poor";
  return "Flat";
}

// ---------------------------------------------------------------------------
// Safety indicators - independent of score
// ---------------------------------------------------------------------------

export type FoilSafetyLevel = "green" | "amber" | "red" | "unavailable";

export type FoilSafetyIndicator = {
  key: string;
  level: FoilSafetyLevel;
  severity: FoilSafetyLevel;
  label: string;
  detail?: string;
  confidence?: "full" | "unavailable";
  provenance?: "missing-local-geometry" | "missing-forecast-input";
};

export function foilSafetyIndicators(input: {
  weatherCode: number;
  waveHeight: number;
  windSpeed: number;
  gustSpeed: number | null;
  currentVelocity: number | null;
  tidePosition: number | null;
  breakType: string;
  visibility: number | null;
}): FoilSafetyIndicator[] {
  const indicators: FoilSafetyIndicator[] = [];

  if (input.weatherCode >= 95) {
    indicators.push({
      key: "prone-lightning",
      level: "red",
      severity: "red",
      label: "Lightning risk",
      detail: "Thunderstorm detected. Do not go on the water. Always check local marine weather warnings.",
    });
  }

  if (input.currentVelocity === null) {
    indicators.push({ key: "prone-current", level: "unavailable", severity: "unavailable", label: "Current data unavailable", detail: "The current forecast is missing, so drift risk cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.currentVelocity > 1.0) {
    indicators.push({
      key: "prone-current",
      level: "red",
      severity: "red",
      label: "Very strong current",
      detail: `Current ${input.currentVelocity.toFixed(1)} m/s. High risk of being swept out.`,
    });
  } else if (input.currentVelocity > 0.5) {
    indicators.push({
      key: "prone-current",
      level: "amber",
      severity: "amber",
      label: "Strong current",
      detail: `Current ${input.currentVelocity.toFixed(1)} m/s. Assess drift risk before entering.`,
    });
  }

  if (input.waveHeight > 3.0) {
    indicators.push({
      key: "prone-swell",
      level: "red",
      severity: "red",
      label: "Extreme swell",
      detail: "Very large swell. Not suitable for foiling.",
    });
  } else if (input.waveHeight > 2.5) {
    indicators.push({
      key: "prone-swell",
      level: "amber",
      severity: "amber",
      label: "Large swell",
      detail: "Experienced riders only. Wipeout forces are significant.",
    });
  }

  if (input.gustSpeed === null) {
    indicators.push({ key: "prone-gusts", level: "unavailable", severity: "unavailable", label: "Gust data unavailable", detail: "The gust forecast is missing, so abrupt wind loading cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.gustSpeed > 50) {
    indicators.push({
      key: "prone-gusts",
      level: "red",
      severity: "red",
      label: "Dangerous gusts",
      detail: `Gusts to ${Math.round(input.gustSpeed)} km/h. Extremely dangerous on a foil.`,
    });
  } else if (input.windSpeed > 35) {
    indicators.push({
      key: "prone-wind",
      level: "amber",
      severity: "amber",
      label: "Strong winds",
      detail: `Sustained ${Math.round(input.windSpeed)} km/h. Strong wind disrupts prone take-off and foil control; experienced prone foilers only.`,
    });
  }

  if (
    (input.breakType === "reef" || input.breakType === "reef break") &&
    input.tidePosition !== null &&
    input.tidePosition < 0.15
  ) {
    indicators.push({
      key: "prone-mast-clearance",
      level: "amber",
      severity: "amber",
      label: "Low tide over reef",
      detail: "Risk of mast/foil striking reef. Check depth before riding.",
    });
  } else if (
    (input.breakType === "reef" || input.breakType === "reef break") &&
    input.tidePosition === null &&
    foilEnergyBase(input.waveHeight) > 0
  ) {
    indicators.push({
      key: "prone-mast-clearance",
      level: "unavailable",
      severity: "unavailable",
      label: "Mast-clearance data unavailable",
      detail: "This is a reef break, but tide depth is unavailable. Check foil clearance locally before entering.",
      confidence: "unavailable",
      provenance: "missing-local-geometry",
    });
  }

  if (input.visibility === null) {
    indicators.push({ key: "prone-visibility", level: "unavailable", severity: "unavailable", label: "Visibility data unavailable", detail: "The visibility forecast is missing, so collision and shore-reference risk cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.visibility < 500) {
    indicators.push({
      key: "prone-visibility",
      level: "red",
      severity: "red",
      label: "Very poor visibility",
      detail: "Visibility below 500m. Dangerous at foiling speeds.",
    });
  } else if (input.visibility < 1000) {
    indicators.push({
      key: "prone-visibility",
      level: "amber",
      severity: "amber",
      label: "Reduced visibility",
      detail: "Visibility below 1km. Use caution at speed.",
    });
  }

  return indicators;
}

// ---------------------------------------------------------------------------
// Safety policy v2 (all-sports engine design 2.7 and 6.2; review WFD-5, WFD-6)
//
// Per-hour rows for the v2 safety module (engines/coastal/safety/foil.ts);
// foilSafetyIndicators above stays the v1 policy, byte for byte. Thresholds are
// v1's except the wind and current tiers (the last two items below); under v2:
// - a missing gust no longer suppresses the sustained strong-wind flag (WFD-6):
//   it adds an "unavailable" gust row and amber still fires above 35 km/h;
// - a missing wind speed, weather code or swell is never read as calm or clear
//   (WFD-5). Only the inputs of v1's rules are reported: v1 has no chop rule, so
//   a missing wind sea adds no row (an "unavailable" row there would mark every
//   hour of a spot without a wind-wave partition unassessable for no rule);
// - the reef mast-clearance flag reads the harmonic tide position (design 2.7:
//   tide-driven v2 flags read tide@harmonic-1), which the module supplies;
// - the current is an optional safety input (owner decision O-07): a missing
//   current is the neutral "no current" and adds no row, while the foil score
//   reads it as a modifier, so the hour is partial there instead;
// - the current is judged by direction at beach breaks (accelerated rollout
//   Phase C preparation, seaward-current.ts): amber above 0.5 m/s in any
//   direction; red when the seaward component is above 1.0 m/s or, at a beach
//   break, the speed is above 2.0 m/s whichever way it sets; at reef, point and
//   river-mouth breaks (and a spot with no recorded type) the speed alone above
//   1.0 m/s, as v1 read it. Open-Meteo's current cell lies offshore of the break
//   and carries the tidal stream along the coast, which at a beach break carries
//   a prone foiler along the beach, not out to sea (H5: 23 of 51 red days were
//   such streams at beach breaks). A caller that supplies no seaward component
//   counts the whole speed as seaward, and no relief unless it asks for one;
// - wind (PRONE_FOIL_V2_WIND, accelerated rollout Phase C preparation): red on
//   gusts of 65 km/h or more or a sustained wind above 50 km/h; amber on a
//   sustained wind above 35 km/h or gusts above 50 km/h. v1 read red on any gust
//   above 50 km/h, but Open-Meteo's gust is the land cell's hourly maximum and
//   runs about 1.8 times the sustained wind, so that tier fired at about 28 km/h
//   sustained, below v1's own amber of 35 (H5: red on 60 of 233 days; on 43 of
//   them the sustained wind never passed 35 km/h). Prone foil has no
//   wind-powered rig, so its gust red sits at the kite's overpower line (owner
//   decision 10).
// ---------------------------------------------------------------------------

/**
 * Prone foil wind tiers under policy v2, in km/h on the forecast's 10 m wind and gust
 * (accelerated rollout Phase C preparation; docs/handover/phase-2c.md):
 * - red on gusts of 65 km/h or more (35 kn, gale-force gusts): they tear a foil board from the
 *   rider and make the foil a projectile in the lineup. The kite reads red on gusts alone at the
 *   same line (owner decision 10), and a prone foiler, with no wind-powered rig, is no more exposed
 *   to a gust than a kiter;
 * - red on a sustained wind above 50 km/h (27 kn, near gale): what v1's 50 km/h gust tier always
 *   caught, since a gust is never below its sustained wind, so a low gust ratio or a missing gust
 *   never hides it;
 * - amber on a sustained wind above 35 km/h (v1) or gusts above 50 km/h (v1's red, which on the
 *   inflated land-cell gust marks a strong, gusty day rather than a dangerous one).
 */
export const PRONE_FOIL_V2_WIND = Object.freeze({
  redGustFromKmh: 65,
  redSustainedAboveKmh: 50,
  amberSustainedAboveKmh: 35,
  amberGustAboveKmh: 50,
});

/** Prone foil current tiers in m/s (v1's), judged by direction at beach breaks (seaward-current.ts). */
export const PRONE_FOIL_V2_CURRENT = Object.freeze({ amberAboveMs: 0.5, redSeawardAboveMs: 1.0 });

export type ProneFoilSafetyV2Input = Readonly<{
  weatherCode: number | null;
  /** Primary swell height (m), unsheltered as in v1; null when the provider had none. */
  swellHeightM: number | null;
  windSpeed: number | null;
  gustSpeed: number | null;
  currentVelocity: number | null;
  /**
   * The current's seaward component in m/s (speed times the cosine of the angle between where it
   * flows and the beach facing, floored at 0). Null or omitted when unresolved: the whole speed then
   * counts as seaward.
   */
  currentSeawardMs?: number | null;
  /**
   * Whether a current along the coast is relieved up to 2.0 m/s: true only at a beach break
   * (seaward-current.ts beachBreakTakesAlongCoastRelief on the recorded spot type, never the
   * scorer's "beach" default). Omitted: no relief, so a new caller never relaxes a red.
   */
  currentAlongCoastRelief?: boolean;
  /** Harmonic tide position 0 (low) to 1 (high), or null. */
  tidePosition: number | null;
  breakType: string;
  visibility: number | null;
  /**
   * The hour's relative humidity in % (model-fog.ts), the visibility row's saturation test
   * (accelerated rollout Phase E). Null or omitted when unknown: the air counts as saturated, so a
   * missing humidity never lowers the row.
   */
  relativeHumidityPct?: number | null;
}>;

function proneMissing(key: string, label: string, detail: string, provenance: FoilSafetyIndicator["provenance"] = "missing-forecast-input"): FoilSafetyIndicator {
  return { key, level: "unavailable", severity: "unavailable", label, detail, confidence: "unavailable", provenance };
}

function proneRow(key: string, level: "amber" | "red", label: string, detail: string): FoilSafetyIndicator {
  return { key, level, severity: level, label, detail };
}

/** Per-hour prone foil rows under safety policy v2. */
export function proneFoilSafetyV2(input: ProneFoilSafetyV2Input): FoilSafetyIndicator[] {
  const rows: FoilSafetyIndicator[] = [];
  const speed = input.windSpeed;
  const gust = input.gustSpeed;

  if (input.weatherCode === null) {
    rows.push(proneMissing("prone-lightning", "Storm forecast unavailable", "The weather-code forecast is missing, so thunderstorm risk cannot be assessed."));
  } else if (input.weatherCode >= 95) {
    rows.push(proneRow("prone-lightning", "red", "Lightning risk", "Thunderstorm detected. Do not go on the water. Always check local marine weather warnings."));
  }

  // O-07: a missing current is the neutral "no current" (no row). Judged by direction at beach
  // breaks (PRONE_FOIL_V2_CURRENT, seaward-current.ts).
  if (input.currentVelocity !== null) {
    const current = classifySuppliedCurrent(input.currentVelocity, input.currentSeawardMs, {
      ...PRONE_FOIL_V2_CURRENT,
      alongCoastRelief: input.currentAlongCoastRelief === true,
    });
    const speed = input.currentVelocity.toFixed(1);
    if (current.level === "red") {
      rows.push(proneRow("prone-current", "red", "Very strong current", current.reason === "seaward" && current.resolved
        ? `Current ${speed} m/s, ${current.seawardMs.toFixed(1)} m/s of it setting offshore. High risk of being swept out.`
        : `Current ${speed} m/s. High risk of being swept out.`));
    } else if (current.level === "amber") {
      rows.push(proneRow("prone-current", "amber", "Strong current", `Current ${speed} m/s. Assess drift risk before entering.`));
    }
  }

  if (input.swellHeightM === null) {
    rows.push(proneMissing("prone-swell", "Swell data unavailable", "The swell forecast is missing, so wave size risk cannot be assessed."));
  } else if (input.swellHeightM > 3.0) {
    rows.push(proneRow("prone-swell", "red", "Extreme swell", "Very large swell. Not suitable for foiling."));
  } else if (input.swellHeightM > 2.5) {
    rows.push(proneRow("prone-swell", "amber", "Large swell", "Experienced riders only. Wipeout forces are significant."));
  }

  // Wind (PRONE_FOIL_V2_WIND): the worst tier the known readings make certain, one row.
  const wind = PRONE_FOIL_V2_WIND;
  if (gust === null) {
    rows.push(proneMissing("prone-gusts", "Gust data unavailable", "The gust forecast is missing, so abrupt wind loading cannot be assessed."));
  }
  if (gust !== null && gust >= wind.redGustFromKmh) {
    rows.push(proneRow("prone-gusts", "red", "Dangerous gusts", `Gusts to ${Math.round(gust)} km/h. Extremely dangerous on a foil.`));
  } else if (speed !== null && speed > wind.redSustainedAboveKmh) {
    rows.push(proneRow("prone-wind", "red", "Very strong wind", `Sustained ${Math.round(speed)} km/h. Near-gale wind; stay off the water on a foil.`));
  } else if (speed !== null && speed > wind.amberSustainedAboveKmh) {
    rows.push(proneRow("prone-wind", "amber", "Strong winds", `Sustained ${Math.round(speed)} km/h. Strong wind disrupts prone take-off and foil control; experienced prone foilers only.`));
  } else if (gust !== null && gust > wind.amberGustAboveKmh) {
    rows.push(proneRow("prone-gusts", "amber", "Strong gusts", `Gusts to ${Math.round(gust)} km/h. Hold the board in the gusts; experienced prone foilers only.`));
  }
  // A missing sustained wind is unavailable unless the gust rules out every tier it could add: the
  // sustained wind never exceeds its gust, so a gust at or below 35 km/h rules out both, and a gust
  // already red leaves nothing for it to add.
  if (speed === null && !(gust !== null && (gust <= wind.amberSustainedAboveKmh || gust >= wind.redGustFromKmh))) {
    rows.push(proneMissing("prone-wind-data", "Wind data unavailable", "The wind forecast is missing, so strong-wind risk cannot be assessed."));
  }

  const reef = input.breakType === "reef" || input.breakType === "reef break";
  if (reef && input.tidePosition !== null && input.tidePosition < 0.15) {
    rows.push(proneRow("prone-mast-clearance", "amber", "Low tide over reef", "Risk of mast/foil striking reef. Check depth before riding."));
  } else if (reef && input.tidePosition === null && (input.swellHeightM === null || foilEnergyBase(input.swellHeightM) > 0)) {
    rows.push(proneMissing("prone-mast-clearance", "Mast-clearance data unavailable",
      "This is a reef break, but tide depth is unavailable. Check foil clearance locally before entering.", "missing-local-geometry"));
  }

  // Model fog (model-fog.ts, accelerated rollout Phase E): red only in saturated air.
  const fog = classifyModelFog({ visibilityM: input.visibility, relativeHumidityPct: input.relativeHumidityPct });
  if (fog === "unavailable") {
    rows.push(proneMissing("prone-visibility", "Visibility data unavailable", "The visibility forecast is missing, so collision and shore-reference risk cannot be assessed."));
  } else if (fog === "red") {
    rows.push(proneRow("prone-visibility", "red", "Very poor visibility", "Visibility below 500m. Dangerous at foiling speeds."));
  } else if (fog === "amber") {
    rows.push(proneRow("prone-visibility", "amber", "Reduced visibility", "Visibility below 1km. Use caution at speed."));
  }

  return rows;
}

// ---------------------------------------------------------------------------
// Hourly input / output types
// ---------------------------------------------------------------------------

export type FoilHourlyInputV5 = {
  waveHeight: number;
  period: number;
  swellDirection: number | null;
  spotOptimalDirection: number | null;
  windSpeed: number;
  gustSpeed: number;
  windDirection: number;
  shoreNormal: number | null;
  windWaveHeight: number;
  tidePosition: number | null;
  breakType: string;
  currentVelocity: number;
  precipitation: number;
  visibility: number;
  weatherCode: number;
  hour: number;
  sunrise: number;
  sunset: number;
  shelterFactor?: number;
};

export type FoilHourlyResult = {
  score: number;
  label: string;
  safety: FoilSafetyIndicator[];
  factors: Readonly<{
    base: number;
    period: number;
    direction: number;
    windSpeed: number;
    gust: number;
    windDirection: number;
    chop: number;
    tide: number;
    current: number;
    rain: number;
    visibility: number;
    goldenHour: number;
  }>;
  readings: Readonly<{ effectiveSwellHeightM: number; effectiveWindWaveHeightM: number }>;
};

// ---------------------------------------------------------------------------
// Hourly scoring function
//
// ARCHITECTURE: Purely multiplicative, then adaptive gamma. No additive
// bonuses, no hard caps.
//
//   product = (energyBase(swell_height) / 100)
//     × periodFactor(swell_period)
//     × directionFactor(swell_dir, spot_optimal_dir)
//     × windSpeedFactor(wind_speed)
//     × gustPenalty(sustained, gust)
//     × windDirectionFactor(wind_dir, shore_normal)
//     × chopFactor(wind_wave_height)
//     × tideFactor(tide_position, break_type)
//     × currentFactor(current_velocity)
//     × rainFactor(precipitation)
//     × visibilityFactor(visibility)
//     × goldenHourFactor(hour, sunrise, sunset, wind_speed)
//
//   hourly_score = 100 × adaptiveGamma(product, LONG_CHAIN)
//
// Each factor is [~0.05, ~1.05]. A factor of 1.0 = no impact. The gamma
// step corrects 11-factor stacking: without it, five factors at ~0.9
// multiply to 0.59 and an honest "all green, nothing perfect" day drops a
// full band. Perfect days (product ~1.0) pass through untouched and true
// write-offs stay in the bottom band.
//
// Score: 0–100 (hourly). Daily: 0–10 (derived from hourly).
// Safety indicators are computed independently and never modify the score.
// ---------------------------------------------------------------------------

export function calculateFoilHourlyV5(input: FoilHourlyInputV5): FoilHourlyResult {
  const shelter = input.shelterFactor ?? 1.0;
  const effectiveSwellH = input.waveHeight * shelter;
  const effectiveWindWaveH = input.windWaveHeight * shelter;

  const base = foilEnergyBase(effectiveSwellH);

  const factors = Object.freeze({
    base: base / 100,
    period: foilPeriodFactor(input.period),
    direction: input.swellDirection !== null
      ? foilDirectionFactor(input.swellDirection, input.spotOptimalDirection)
      : DIRECTION_UNKNOWN_DEFAULT,
    windSpeed: foilWindSpeedFactor(input.windSpeed),
    gust: foilGustPenalty(input.windSpeed, input.gustSpeed),
    windDirection: foilWindDirectionFactor(input.windDirection, input.shoreNormal),
    chop: foilChopFactor(effectiveWindWaveH),
    tide: input.tidePosition !== null
      ? foilTideFactor(input.tidePosition, input.breakType)
      : 1.0,
    current: foilCurrentFactor(input.currentVelocity),
    rain: foilRainFactor(input.precipitation),
    visibility: foilVisibilityFactor(input.visibility),
    goldenHour: foilGoldenHourFactor(input.hour, input.sunrise, input.sunset, input.windSpeed),
  });

  const raw = 100 * composeScore(PRONE_FOIL_CHAIN, factors).compressed;
  const score = Math.round(Math.max(0, Math.min(100, raw)) * 10) / 10;

  const safety = foilSafetyIndicators({
    weatherCode: input.weatherCode,
    waveHeight: input.waveHeight,
    windSpeed: input.windSpeed,
    gustSpeed: input.gustSpeed,
    currentVelocity: input.currentVelocity,
    tidePosition: input.tidePosition,
    breakType: input.breakType,
    visibility: input.visibility,
  });

  return {
    score,
    label: foilLabelV5(score / 10),
    safety,
    factors,
    readings: Object.freeze({ effectiveSwellHeightM: effectiveSwellH, effectiveWindWaveHeightM: effectiveWindWaveH }),
  };
}

// ---------------------------------------------------------------------------
// Daily derivation: 35% daylight average + 65% best 3h window
//
// Foiling sessions are typically 1–2 hours. The best window matters most,
// but persistent bad conditions (storms all day with one brief clearing)
// should still drag the daily score down.
//
// Input: hourly scores on 0–100 scale.
// Output: daily score on 0–10 scale.
// ---------------------------------------------------------------------------

export function deriveFoilDailyV5(
  hourlyScores: { hour: number; score: number; isDay: boolean }[],
): { overall: number; bestWindowStart: number | null; bestWindowScores: number[] } {
  const daily = summariseDailyQuality(
    hourlyScores.map((point) => ({ ...point, isDay: point.isDay })),
    DAILY_BEST_WINDOW_HOURS, DAILY_BEST_WINDOW_WEIGHT,
  );
  return {
    overall: Math.round((daily.overall / 10) * 10) / 10,
    bestWindowStart: daily.bestWindowStart,
    bestWindowScores: daily.bestWindowScores,
  };
}

// ---------------------------------------------------------------------------
// Re-exports
// ---------------------------------------------------------------------------

export { isDark };
export type { SunTimes };
